import {
  Component,
  computed,
  inject,
  input,
  output,
} from '@angular/core';
import { Router } from '@angular/router';

import { SideNavState } from '../../menu-types';
import { MenuService } from 'src/app/services/menu.service';
import { CourseStore } from 'src/app/stores/course-store';
import { CampaignStore } from 'src/app/stores/campaign-store';
import {
  MenuItemViewModel,
  MenuSectionConfig,
  featureToViewModel,
  courseToViewModel,
  campaignToViewModel,
} from '../../menu-grammar';
import { MenuSectionComponent } from '../../menu-section/menu-section.component';

@Component({
  selector: 'app-free-taster-menu',
  templateUrl: './free-taster-menu.component.html',
  styleUrl: './free-taster-menu.component.scss',
  imports: [MenuSectionComponent],
})
export class FreeTasterMenuComponent {
  private router = inject(Router);
  private courseStore = inject(CourseStore);
  private menuService = inject(MenuService);
  private campaignStore = inject(CampaignStore);

  sidenavState = input.required<SideNavState>();
  iconOnly = computed(() => this.sidenavState() === SideNavState.SLIM);

  /** Mobile's sidenav is an overlay drawer — parent closes it on receipt (see side-nav-menu). */
  closeSidenav = output<void>();

  selectedCourseId: string | number | null = null;

  featureSection = computed<MenuSectionConfig>(() => ({
    key: 'features',
    items: this.menuService
      .menuItems()
      .filter((item) => this.menuService.canViewMenuItem(item.requires))
      .map((item) =>
        featureToViewModel(item, this.menuService.isMenuItemLocked(item)),
      ),
  }));

  courseSection = computed<MenuSectionConfig>(() => ({
    key: 'courses',
    items: this.courseStore
      .lockedCourses()
      .map((course) =>
        courseToViewModel(course, true),
      )
      .sort((a, b) => a.title.localeCompare(b.title)),
    searchable: true,
    searchPlaceholder: 'Search courses...',
  }));

  campaignSection = computed<MenuSectionConfig>(() => ({
    key: 'campaigns',
    items: this.campaignStore.campaigns().map((campaign) => campaignToViewModel(campaign)),
  }));

  /** Static single-item section — shown to every unsubscribed, non-onboarding user. */
  packagesSection = computed<MenuSectionConfig>(() => ({
    key: 'packages',
    items: [
      {
        id: 'packages',
        icon: 'inventory_2',
        iconType: 'material',
        title: 'Packages',
        url: '/packages',
        locked: false,
      },
    ],
  }));

  ngOnInit() {
    this.campaignStore.fetch();
  }

  onCourseSelect(item: MenuItemViewModel) {
    this.selectedCourseId = item.id ?? null;
    this.router.navigate([item.url]);
    this.closeSidenavOnMobile();
  }

  /** Clears a stale course highlight left over from before navigating here. */
  onOtherSectionSelect(item: MenuItemViewModel) {
    this.selectedCourseId = null;
    this.router.navigate([item.url]);
    this.closeSidenavOnMobile();
  }

  private closeSidenavOnMobile(): void {
    if (this.sidenavState() === SideNavState.MOBILE) {
      this.closeSidenav.emit();
    }
  }
}
